import BackButton from "@components/buttons/BackButton";
import CustomButton from "@components/buttons/CustomButton";
import CustomDateField from "@components/form/CustomDateField";
import CustomFileField from "@components/form/CustomFileField";
import CustomInputField from "@components/form/CustomInputField";
import CustomTextAreaField from "@components/form/CustomTextAreaField";
import { LoadingSpinner } from "@components/layouts/LoadingSpinner";
import PageWrapper from "@components/layouts/PageWrapper";
import { Toast } from "@helpers/popups/Toast";
import { ZNews, newsSchema } from "@helpers/validation-schemas/cms/news.schema";
import { zodResolver } from "@hookform/resolvers/zod";
import useGetPermission from "@hooks/common/useGetPermission";
import { useErrorHandling } from "@hooks/useErrorHandling";
import { useCreateNewsMutation } from "@states/cms/news.api";
import { TFailResponse } from "@type-defs/general/TFailResponse";
import { TSuccessResponse } from "@type-defs/general/TSuccessResponse";
import { NEWS } from "@utils/constants/cms/news.constants";
import { castTypeArr } from "@utils/general/cast-type";
import React from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { Navigate, useNavigate } from "react-router-dom";

const parseSocialLinksInput = (value?: string) => {
  if (!value?.trim()) return [];

  return value
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const [rawLabel, ...rawUrl] = line.split("|");

      if (rawUrl.length === 0) {
        return {
          label: "",
          url: rawLabel.trim(),
        };
      }

      return {
        label: rawLabel.trim(),
        url: rawUrl.join("|").trim(),
      };
    })
    .filter((item) => item.url);
};

const toApiDate = (value: unknown) => {
  if (!value) return "";
  const date = new Date(value as string | Date);
  if (Number.isNaN(date.getTime())) return "";
  return date.toISOString().split("T")[0];
};

function NewsAddPage() {
  const navigate = useNavigate();
  const { hasAccess } = useGetPermission();
  const canCreate =
    hasAccess("create", "CMS", "News") || hasAccess("create", "Others", "News");
  const {
    register,
    handleSubmit,
    setError,
    setValue,
    watch,
    control,
    formState: { errors, isSubmitting },
  } = useForm<ZNews>({
    resolver: zodResolver(newsSchema),
    defaultValues: {
      status: 1,
      social_links_text: "",
    },
  });

  const [createNews, { isLoading, error: createError }] =
    useCreateNewsMutation();

  useErrorHandling(...castTypeArr<TFailResponse>([createError]));

  const onSubmit: SubmitHandler<ZNews> = async (data) => {
    try {
      const formData = new FormData();
      formData.append("title", data.title);
      formData.append("content", data.content);

      const fromDate = toApiDate(data.from_date);
      const toDate = toApiDate(data.to_date);

      if (fromDate) {
        formData.append("from_date", fromDate);
      }

      if (toDate) {
        formData.append("to_date", toDate);
      }

      if (data.status !== undefined && data.status !== null) {
        formData.append("status", String(data.status));
      }

      formData.append(
        "social_links",
        JSON.stringify(parseSocialLinksInput(data.social_links_text))
      );

      const image = (data as any).image?.[0];
      if (image) {
        formData.append("image", image);
      }

      const res = await createNews(formData);
      const successData = "data" in res ? (res.data as TSuccessResponse) : null;

      if (successData?.success) {
        await Toast.fire({
          title: successData.message,
          icon: "success",
        });

        navigate(NEWS.BASE_PATH, {
          state: {
            reload: true,
          },
        });
      }
    } catch (error: any) {
      setError("root", {
        message: error.message,
      });
    }
  };

  if (!canCreate) {
    return <Navigate to="/access-denied" />;
  }

  return (
    <PageWrapper>
      {isLoading && <LoadingSpinner />}
      <BackButton />
      <form onSubmit={handleSubmit(onSubmit)}>
        <h3 className="mb-4 ml-2 text-2xl font-[600] uppercase text-primary-dark">
          {NEWS.ADD_NEW_TITLE}
        </h3>

        <div className="grid grid-cols-6 gap-x-4 gap-y-8">
          <CustomInputField
            label="Title"
            errors={errors}
            name="title"
            placeHolder="Enter title"
            register={register}
            required
            colSpan={3}
          />
          <CustomDateField
            name="from_date"
            label="Start Date"
            control={control as any}
            register={register}
            errors={errors}
            colSpan={1}
          />
          <CustomDateField
            name="to_date"
            label="End Date"
            control={control as any}
            register={register}
            errors={errors}
            colSpan={1}
          />
        </div>

        <div className="grid grid-cols-6 gap-x-4 gap-y-8">
          <CustomTextAreaField
            label="Content"
            errors={errors}
            name="content"
            placeHolder="Enter content"
            register={register}
            required
            colSpan={6}
            rows={5}
          />
        </div>

        <div className="grid grid-cols-6 gap-x-4 gap-y-8">
          <CustomTextAreaField
            label="Social Links"
            errors={errors}
            name="social_links_text"
            placeHolder="One per line. Use label|url or just url"
            register={register}
            colSpan={6}
            rows={4}
          />
        </div>

        <div className="grid grid-cols-6 gap-x-4 gap-y-8">
          <CustomFileField
            label="Cover Image"
            errors={errors}
            name="image"
            register={register}
            setValue={setValue}
            watch={watch}
            colSpan={3}
          />
        </div>

        <div className="ml-2 mt-14">
          <CustomButton
            isLarge
            title="Cancel"
            color="white"
            textColor="gray.800"
            className="mr-10"
            handleClick={() => navigate(-1)}
          />
          <CustomButton
            handleClick={handleSubmit(onSubmit)}
            isLarge
            title={isSubmitting ? "Loading..." : "Submit"}
            disabled={isSubmitting}
            type="submit"
          />
        </div>

        {errors.root && (
          <div className="mt-6 text-red-500">{errors.root.message}</div>
        )}
      </form>
    </PageWrapper>
  );
}

export default NewsAddPage;
